import { useEffect, useMemo, useRef } from 'react'

import { useRemoteParticipants, useTracks } from '@livekit/components-react'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import type { RemoteParticipant } from 'livekit-client'
import { Track } from 'livekit-client'

import useLocalStorage from 'modules/core/lib/useLocalStorage'

// Extend Window interface for webkit audio context
declare global {
  interface Window {
    webkitAudioContext?: typeof AudioContext
  }
}

/**
 * Hook to manage meeting sound settings
 */
export function useMeetingSoundsSettings() {
  const [enabled, setEnabled] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS.meetingSoundsEnabled,
    true
  )

  return { enabled, setEnabled }
}

/**
 * Hook to play sound notifications when participants join or leave the meeting
 */
export function useMeetingSounds(enabled = true) {
  const participants = useRemoteParticipants()
  const previousParticipantsRef = useRef<Set<string>>(new Set())
  const audioContextRef = useRef<AudioContext | null>(null)
  const isInitializedRef = useRef(false)

  // Track screen share changes
  const screenShareTracks = useTracks([
    { source: Track.Source.ScreenShare, withPlaceholder: false },
  ])
  const previousScreenSharesRef = useRef<Set<string>>(new Set())
  const isScreenShareInitializedRef = useRef(false)

  // Memoize participant IDs to avoid unnecessary re-renders
  const participantIds = useMemo(
    () => new Set(participants.map((p: RemoteParticipant) => p.identity)),
    [participants.map((p) => p.identity).join(',')]
  )

  // Memoize screen share participant IDs
  const screenShareIds = useMemo(
    () => new Set(screenShareTracks.map((track) => track.participant?.identity).filter(Boolean)),
    [screenShareTracks.map((track) => track.participant?.identity).join(',')]
  )

  // Create audio context on first use
  const getAudioContext = () => {
    if (!audioContextRef.current) {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext
      if (AudioContextClass) {
        audioContextRef.current = new AudioContextClass()
      }
    }
    return audioContextRef.current
  }

  // Create a simple tone using Web Audio API
  const playTone = (frequency: number, duration: number, volume = 0.3) => {
    if (!enabled) return

    try {
      const audioContext = getAudioContext()

      if (!audioContext) return

      // Resume audio context if it's suspended (required by browser autoplay policies)
      if (audioContext.state === 'suspended') {
        audioContext.resume()
      }

      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)

      oscillator.frequency.value = frequency
      oscillator.type = 'sine'

      // Set volume with a smooth envelope
      gainNode.gain.setValueAtTime(0, audioContext.currentTime)
      gainNode.gain.linearRampToValueAtTime(volume, audioContext.currentTime + 0.01)
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + duration)

      oscillator.start(audioContext.currentTime)
      oscillator.stop(audioContext.currentTime + duration)
    } catch (error) {
      // Silently handle audio context errors
    }
  }

  // Play join sound (pleasant ascending tone)
  const playJoinSound = () => {
    playTone(523.25, 0.2) // C5
    setTimeout(() => playTone(659.25, 0.3), 100) // E5
  }

  // Play leave sound (gentle descending tone)
  const playLeaveSound = () => {
    playTone(659.25, 0.2) // E5
    setTimeout(() => playTone(523.25, 0.3), 100) // C5
  }

  // Play screen share start sound (tech-like ascending sequence)
  const playScreenShareStartSound = () => {
    playTone(440, 0.15, 0.25) // A4
    setTimeout(() => playTone(554.37, 0.15, 0.25), 80) // C#5
    setTimeout(() => playTone(659.25, 0.2, 0.3), 160) // E5
  }

  // Play screen share stop sound (gentle tech-like descending)
  const playScreenShareStopSound = () => {
    playTone(659.25, 0.15, 0.25) // E5
    setTimeout(() => playTone(554.37, 0.15, 0.25), 80) // C#5
    setTimeout(() => playTone(440, 0.2, 0.2), 160) // A4
  }

  // Handle participant join/leave
  useEffect(() => {
    const previousParticipantIds = previousParticipantsRef.current

    // Skip on first render to avoid playing sounds for existing participants
    if (!isInitializedRef.current) {
      isInitializedRef.current = true
      previousParticipantsRef.current = participantIds
      return
    }

    // Find participants who joined
    const joinedParticipants = [...participantIds].filter((id) => !previousParticipantIds.has(id))

    // Find participants who left
    const leftParticipants = [...previousParticipantIds].filter((id) => !participantIds.has(id))

    // Play sounds for changes
    if (joinedParticipants.length > 0) {
      playJoinSound()
    }

    if (leftParticipants.length > 0) {
      playLeaveSound()
    }

    // Update previous participants
    previousParticipantsRef.current = participantIds
  }, [participantIds])

  // Handle screen share start/stop
  useEffect(() => {
    const previousScreenShares = previousScreenSharesRef.current

    // Skip on first render to avoid playing sounds for existing screen shares
    if (!isScreenShareInitializedRef.current) {
      isScreenShareInitializedRef.current = true
      previousScreenSharesRef.current = screenShareIds
      return
    }

    // Find participants who started screen sharing
    const startedScreenShare = [...screenShareIds].filter((id) => !previousScreenShares.has(id))

    // Find participants who stopped screen sharing
    const stoppedScreenShare = [...previousScreenShares].filter((id) => !screenShareIds.has(id))

    // Play sounds for screen share changes
    if (startedScreenShare.length > 0) {
      playScreenShareStartSound()
    }

    if (stoppedScreenShare.length > 0) {
      playScreenShareStopSound()
    }

    // Update previous screen shares
    previousScreenSharesRef.current = screenShareIds
  }, [screenShareIds])

  // Cleanup audio context on unmount
  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close()
      }
    }
  }, [])
}
